import { useState, type FormEvent } from 'react';
import { alphabetical } from '../shared/order';
import { data, useTable } from '@core/store';
import { Modal } from '@core/ui/Modal';
import { Button } from '@core/ui/Button';
import { FormError, ModalFooter, fieldInput as input } from '@core/ui/form';
import { TogglePill } from '@core/ui/TogglePill';
import { planLanesSave, type LaneDraft } from './planner';

const FORM_ID = 'lanes-form'; // Save живёт в футере окна, вне формы

/** Редактор лент Timeline: имя, проекты галочками, события как контекст, порядок, удаление. Releases — такая же лента. */
export function LanesModal({ onClose }: { onClose: () => void }) {
  const lanes = useTable('timeline_lane');
  const links = useTable('lane_project');
  const projects = useTable('project');
  const [draft, setDraft] = useState<LaneDraft[]>(() => [...lanes.values()]
    .sort((a, b) => a.position - b.position)
    .map((l) => ({
      id: l.id, name: l.name, showEvents: l.show_events, showLaunches: l.show_launches,
      projectIds: [...links.values()].filter((x) => x.lane_id === l.id).map((x) => x.project_id),
    })));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const projectList = alphabetical(projects.values());
  const patch = (i: number, p: Partial<LaneDraft>) => setDraft(draft.map((d, j) => (j === i ? { ...d, ...p } : d)));
  const move = (i: number, dir: -1 | 1) => { const j = i + dir; if (j < 0 || j >= draft.length) return; const next = [...draft]; [next[i], next[j]] = [next[j]!, next[i]!]; setDraft(next); };

  async function save(e: FormEvent) {
    e.preventDefault();
    const plan = planLanesSave(draft, [...lanes.values()], [...links.values()], () => crypto.randomUUID());
    if ('error' in plan) { setError(plan.error); return; }
    setBusy(true); setError(null);
    const r = plan.changes.length ? await data.applyChanges(plan.changes, { kind: 'edit_lanes' }) : { ok: true as const };
    setBusy(false);
    if (!r.ok) { setError(r.error); return; }
    onClose();
  }

  const footer = <ModalFooter form={FORM_ID} busy={busy} onCancel={onClose} />;
  return (
    <Modal open onClose={onClose} title="Timeline lanes" width="max-w-3xl" footer={footer}>
      <form id={FORM_ID} onSubmit={(e) => void save(e)} className="flex flex-col gap-4" data-lanes-form>
        <p className="text-sm text-gray-600">A lane is a name and a set of projects; a project can be in several lanes and is also assigned from its own card. A project in no lane does not appear on the Timeline: after deleting a lane, add its projects to another lane if you want to keep seeing them. Events show as context in lanes where they are switched on; launch releases appear in lanes with «Show launches».</p>
        {draft.map((d, i) => (
          <fieldset key={d.id ?? `new-${i}`} className="border border-line rounded-md p-3 flex flex-col gap-2" data-lane-draft>
            <div className="flex items-center gap-2">
              <input className={`${input} max-w-xs`} value={d.name} placeholder="Lane name" aria-label="Lane name" onChange={(e) => patch(i, { name: e.target.value })} />
              <label className="flex items-center gap-2 text-sm ml-2"><input type="checkbox" className="w-4 h-4" checked={d.showEvents} onChange={(e) => patch(i, { showEvents: e.target.checked })} />Show events</label>
              <label className="flex items-center gap-2 text-sm ml-2" title="Launch tasks with the Release status, on the week they end"><input type="checkbox" className="w-4 h-4" checked={d.showLaunches} onChange={(e) => patch(i, { showLaunches: e.target.checked })} />Show launches</label>
              <span className="ml-auto flex items-center gap-1">
                <Button variant="ghost" size="sm" className="px-2" onClick={() => move(i, -1)} disabled={i === 0} aria-label="Move up" title="Move up">↑</Button>
                <Button variant="ghost" size="sm" className="px-2" onClick={() => move(i, 1)} disabled={i === draft.length - 1} aria-label="Move down" title="Move down">↓</Button>
                <Button variant="ghost" size="sm" className="px-2 text-danger" onClick={() => setDraft(draft.filter((_, j) => j !== i))} aria-label={`Delete lane ${d.name}`} title="Delete the lane. Its projects leave the Timeline until you add them to another lane">×</Button>
              </span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {projectList.map((p) => {
                const on = d.projectIds.includes(p.id);
                return <TogglePill key={p.id} on={on} label={p.name} color={p.color} data={{ 'data-lane-project': p.id }} onClick={() => patch(i, { projectIds: on ? d.projectIds.filter((x) => x !== p.id) : [...d.projectIds, p.id] })} />;
              })}
            </div>
          </fieldset>
        ))}
        <Button variant="secondary" className="self-start" onClick={() => setDraft([...draft, { id: null, name: '', projectIds: [], showEvents: false, showLaunches: false }])}>+ Lane</Button>
        <FormError error={error} />
      </form>
    </Modal>
  );
}
